import { isCallCenterUser } from "../../../services/userService";
import { doesHaveCoverage } from "../../../services/coverageService";
import { shouldShowContractConsent } from "../../../services/contractService";
import { sortBy } from "lodash-es";
import { isNotNullOrWhitespace } from "../../../services/stringService";

// This function should be updated to use a configuration value. I'm hardcoding this for LTC for now for the
// purposes of the demo
function shouldCoverageSkipCoreInterview(configuration, assistanceCategories) {
    if (configuration.canNavigateToMarketplaceWithCoverage) {
        return false;
    }
    return assistanceCategories.indexOf("Medicaid Long Term Care") < 0;
}

export const pageUrlMapping = {
    entry: {
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => true,
        order: 10,
        url: "entry",
    },
    verification: {
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => true,
        order: 15,
        url: "verification",
    },
    contractConsent: {
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            if (shouldShowContractConsent(interview) && !isCallCenterUser(user)) {
                return true;
            }
            return false;
        },
        order: 20,
        url: "contractConsent",
    },
    coverage: {
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => true,
        order: 30,
        url: "coverage",
    },
    patientInfoScreening: {
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            if (shouldCoverageSkipCoreInterview(configuration, customerAssistanceCategories) === true) {
                if (interview.coverage.isFullyCovered) {
                    return false;
                }

                if (doesHaveCoverage(interview)) {
                    return false;
                }
            }

            return true;
        },
        order: 40,
        url: "patientInfoScreening",
    },
    householdMatrix: {
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            if (shouldCoverageSkipCoreInterview(configuration, customerAssistanceCategories) === true) {
                if (interview.coverage.isFullyCovered) {
                    return false;
                }
                if (doesHaveCoverage(interview)) {
                    return false;
                }
            }

            if (interview.patientInfoScreening?.isContinuing === false) {
                return false;
            }

            return true;
        },
        order: 50,
        url: "household-builder",
    },
    personInfoScreening: {
        order: 60,
        url: "personInfoScreening",
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            if (shouldCoverageSkipCoreInterview(configuration, customerAssistanceCategories) === true) {
                if (interview.coverage.isFullyCovered) {
                    return false;
                }
                if (doesHaveCoverage(interview)) {
                    return false;
                }
            }

            if (interview.patientInfoScreening?.isContinuing === false) {
                return false;
            }

            return true;
        },
    },
    government: {
        order: 65,
        url: "government",
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            if (configuration?.programSections == null) {
                return false;
            }

            if (shouldCoverageSkipCoreInterview(configuration, customerAssistanceCategories) === true) {
                if (doesHaveCoverage(interview)) {
                    return false;
                }
            }

            const programSections = configuration.programSections;
            const isShown = isNotNullOrWhitespace(programSections.government);
            return isShown;
        },
    },
    financialAssistance: {
        order: 65,
        url: "financialAssistance",
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            if (configuration?.programSections == null) {
                return false;
            }

            if (shouldCoverageSkipCoreInterview(configuration, customerAssistanceCategories) === true) {
                if (doesHaveCoverage(interview)) {
                    return false;
                }
            }

            const programSections = configuration.programSections;
            const isShown = isNotNullOrWhitespace(programSections.financialAssistance);
            if (isShown === false) {
                return false;
            }

            if (programSections.financialAssistance === "UMC_FA") {
                let potentialStudents = [...interview.householdMembers];
                if (interview.entry.responsibleParty?.personId) {
                    potentialStudents = [...interview.householdMembers, interview.entry.responsibleParty];
                }

                potentialStudents = potentialStudents
                    .filter(x => x.personId !== interview.entry?.patient?.personId)
                    .filter(x => x.age >= 19 && x.age <= 26);

                return potentialStudents.length > 0;
            }

            return true;
        },
    },
    longTermCare: {
        order: 67,
        url: "longTermCare",
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            return customerAssistanceCategories.indexOf("Medicaid Long Term Care") >= 0;
        },
    },
    exchange: {
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            if (interview.coverage.isFullyCovered) {
                return false;
            }
            if (shouldCoverageSkipCoreInterview(configuration, customerAssistanceCategories) === false) {
                if (doesHaveCoverage(interview)) {
                    return false;
                }
            }

            if (interview.patientInfoScreening?.isContinuing === false) {
                return false;
            }
            return customerAssistanceCategories?.indexOf("Exchange") >= 0;
        },
        order: 70,
        url: "exchange",
    },
    documentation: {
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            return true;
        },
        order: 80,
        url: "documentation",
    },
    marketplace: {
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            if (shouldCoverageSkipCoreInterview(configuration, customerAssistanceCategories) === true) {
                if (interview.coverage.isFullyCovered) {
                    return false;
                }
            }
            return !isCallCenterUser(user);
        },
        order: 90,
        url: "details/marketplace",
    },
    thankYou: {
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            return isCallCenterUser(user);
        },
        order: 100,
        url: "thankYou",
    },
    dispatch: {
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            return !isCallCenterUser(user);
        },
        order: 110,
        url: "details/dispatch",
    },
    formsAndApplications: {
        shouldBeVisible: (interview, configuration, customerAssistanceCategories, user, person, customer) => {
            return customerAssistanceCategories.indexOf("Medicaid Long Term Care") >= 0;
        },
        order: 120,
        url: "formsAndApplications",
    },
};

function createUrlBase(matchUrl, pageToNavigate) {
    const urlToNavigate = pageToNavigate?.url;
    return createUrlBaseString(matchUrl, urlToNavigate);
}

function createUrlBaseString(matchUrl, urlToNavigate) {
    if (urlToNavigate == null) {
        throw new Error("Url to navigate not found");
    }
    return `${matchUrl}/${urlToNavigate}`;
}

export function getSectionsToBeShown(interview, configuration, customerAssistanceCategories, user, person, customer) {
    let sectionsToBeShown = {};
    for (const pageKey in pageUrlMapping) {
        const pageObject = pageUrlMapping[pageKey];
        sectionsToBeShown[pageKey] = pageObject.shouldBeVisible(
            interview,
            configuration,
            customerAssistanceCategories,
            user,
            person,
            customer
        );
    }
    return sectionsToBeShown;
}

export function getNextPage(
    matchUrl,
    currentPageName,
    interview,
    configuration,
    assistanceCategories,
    user,
    person,
    customer,
    shouldContinue
) {
    return getNavigationPage(
        matchUrl,
        currentPageName,
        interview,
        configuration,
        assistanceCategories,
        user,
        person,
        customer,
        true,
        shouldContinue
    );
}

export function getPreviousPage(
    matchUrl,
    currentPageName,
    interview,
    configuration,
    assistanceCategories,
    user,
    person,
    customer
) {
    return getNavigationPage(
        matchUrl,
        currentPageName,
        interview,
        configuration,
        assistanceCategories,
        user,
        person,
        customer,
        false
    );
}

function getNavigationPage(
    matchUrl,
    currentPageName,
    interview,
    configuration,
    assistanceCategories,
    user,
    person,
    customer,
    gotoNext = true,
    shouldContinue
) {
    // This is a hack. We need to move this logic to the redux store somehow (Might need sagas)
    if (currentPageName === "patientInfoScreening" && gotoNext === true) {
        interview = {
            ...interview,
            patientInfoScreening: {
                ...interview.patientInfoScreening,
                isContinuing: shouldContinue,
            },
        };
    }
    const createUrl = createUrlBase.bind(this, matchUrl);
    const sectionsShown = getSectionsToBeShown(interview, configuration, assistanceCategories, user, person, customer);
    const pageUrls = Object.keys(pageUrlMapping).filter(x => sectionsShown[x] === true);
    const sortedPageUrls = sortBy(pageUrls, x => {
        const item = pageUrlMapping[x];
        return item.order;
    });
    const indexOfCurrentSection = sortedPageUrls.indexOf(currentPageName);
    const nextSection =
        gotoNext === true ? sortedPageUrls[indexOfCurrentSection + 1] : sortedPageUrls[indexOfCurrentSection - 1];
    if (nextSection == null) {
        return createUrl(pageUrlMapping[currentPageName]);
    }

    return createUrl(pageUrlMapping[nextSection]);
}

export function getFurthestPage(matchUrl, currentPage, interview, configuration) {
    const createUrl = createUrlBaseString.bind(this, matchUrl);
    const wizardStatus = interview.wizard;
    if (wizardStatus.exchange) {
        return createUrl("exchange");
    }
    if (wizardStatus.personInfoScreening) {
        return createUrl("personInfoScreening");
    }
    if (wizardStatus.householdMatrix) {
        return createUrl("householdMatrix");
    }
    if (wizardStatus.patientInfoScreening) {
        return createUrl("patientInfoScreening");
    }
    if (wizardStatus.coverage) {
        return createUrl("coverage");
    }
    if (wizardStatus.coverage) {
        return createUrl("coverage");
    }

    return createUrl("verification");
}
